import React from 'react';
import {
  ContentItem,
  CharacterPortrait,
  ChoiceItem,
  DialogueItem,
  CommandItem,
  TextOverlayItem,
  ChapterEndItem,
  ChapterEndPerfRow,
  TextOverlayOption,
  PhoneMailItem,
  PhoneCallItem,
  PhoneChatItem,
  PhoneMailInteractItem,
  SituationSortGameItem,
  PasswordGameItem,
  DragDropGameItem,
  TimedOverlayItem,
  ConfidenceGainItem,
  GameOverItem,
  RecordCardItem,
  SceneElement,
} from '../../types';
import { JsonEditor } from './JsonEditor';
import {
  CharacterPlacement,
  buildMoodsFromPortrait,
  defaultMoodFor,
  getDialoguePlacement,
  syncDialogueCharacter,
} from './character-placement';
import { listConfiguredEmotions } from './emotion-presets';
import {
  PhoneCallMessagesEditor,
  PhoneReplyOptionsEditor,
} from './PhoneConversationEditors';
import {
  PhoneMailAttachmentsEditor,
  PhoneMailInboxItemsEditor,
  PhoneMailTriageActionsEditor,
  PhoneMailTriageMailsEditor,
} from './PhoneMailEditors';
import { SituationSortGameEditor } from './SituationSortEditors';
import { defaultChapterEndData } from '../../systems/chapter-end-content';
import { formatGameOverText } from '../../systems/game-over-text';
import { DialoguePreview } from './DialoguePreview';
import { PedaCategorySelect } from './PedaCategorySelect';
import { resolvePhoneCallStyle } from '../../systems/phone-call-style';
import styles from './scenario-studio.module.css';

interface ContentItemFormProps {
  item: ContentItem;
  scenarioId: string;
  sceneBackground?: string;
  sceneIds: string[];
  characters: CharacterPortrait[];
  sceneElements?: SceneElement[];
  onChange: (item: ContentItem, elements?: SceneElement[]) => void;
}

function SceneSelect({
  value,
  sceneIds,
  onChange,
  label = 'goto',
}: {
  value?: string;
  sceneIds: string[];
  onChange: (v: string) => void;
  label?: string;
}) {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <select className={styles.select} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {sceneIds.map((id) => (
          <option key={id} value={id}>{id}</option>
        ))}
      </select>
    </div>
  );
}

const PLACEMENT_LABELS: Record<CharacterPlacement, string> = {
  gauche: 'Gauche',
  centre: 'Centre',
  droite: 'Droite',
};

function DialogueForm({
  item,
  scenarioId,
  sceneBackground,
  sceneIds,
  characters,
  sceneElements,
  onChange,
}: ContentItemFormProps & { item: DialogueItem }) {
  const placement = item.placement ?? getDialoguePlacement(item, sceneElements);
  const selectedChar = item.characterId
    ? characters.find((c) => c.id === item.characterId)
    : undefined;
  const moodOptions = selectedChar
    ? (() => {
        const keys = listConfiguredEmotions(selectedChar.emotions);
        if (keys.length > 0) return keys;
        return selectedChar.defaultImage ? (['neutre'] as const) : [];
      })()
    : [];
  const currentEmotion =
    item.emotion ?? item.characterMood?.mood ?? (moodOptions.includes('neutre') ? 'neutre' : moodOptions[0] ?? 'neutre');

  const applyCharacterSync = (
    char: CharacterPortrait,
    newPlacement: CharacterPlacement,
    patch: Partial<DialogueItem> = {}
  ) => {
    const base = { ...item, ...patch };
    const { dialogue } = syncDialogueCharacter(base, sceneElements, char, newPlacement);
    onChange(dialogue);
  };

  const updatePlacement = (newPlacement: CharacterPlacement) => {
    if (!item.characterId || !selectedChar) return;
    onChange({ ...item, placement: newPlacement });
  };

  const updateEmotion = (emotion: string) => {
    if (!selectedChar || !item.characterId) {
      onChange({ ...item, emotion: emotion || 'neutre' });
      return;
    }
    const mood = emotion || 'neutre';
    const moods = buildMoodsFromPortrait(selectedChar);
    const resolvedMood = moods[mood] ? mood : defaultMoodFor(selectedChar, mood);

    onChange({
      ...item,
      emotion: resolvedMood,
      characterMood: undefined,
    });
  };

  return (
    <>
      <div className={styles.field}>
        <label>
          <input
            type="checkbox"
            checked={item.isInternal === true}
            onChange={(e) => {
              const isInternal = e.target.checked || undefined;
              onChange({ ...item, isInternal, ...(isInternal ? { characterMood: undefined } : {}) });
            }}
          />
          {' '}Monologue intérieur (pas de sprite)
        </label>
      </div>

      {!item.isInternal && (
        <>
          <div className={styles.field}>
            <label>Personnage</label>
            <select
              className={styles.select}
              value={item.characterId ?? ''}
              onChange={(e) => {
                const charId = e.target.value;
                if (!charId) {
                  onChange({
                    ...item,
                    characterId: undefined,
                    characterMood: undefined,
                    speaker: item.speaker,
                  });
                  return;
                }
                const char = characters.find((c) => c.id === charId);
                if (!char) return;
                applyCharacterSync(char, placement, { characterId: charId });
              }}
            >
              <option value="">— Aucun —</option>
              {characters.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
              ))}
            </select>
            {characters.length === 0 && (
              <p className={styles.hint}>Ajoutez des personnages dans l&apos;onglet Personnages.</p>
            )}
          </div>

          {selectedChar && (
            <>
              <div className={styles.field}>
                <label>Placement à l&apos;écran</label>
                <div className={styles.placementRow}>
                  {(['gauche', 'centre', 'droite'] as CharacterPlacement[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`${styles.placementBtn} ${placement === p ? styles.placementBtnActive : ''}`}
                      onClick={() => updatePlacement(p)}
                      aria-pressed={placement === p}
                    >
                      {PLACEMENT_LABELS[p]}
                    </button>
                  ))}
                </div>
                <p className={styles.placementCurrent}>
                  Sélectionné : <strong>{PLACEMENT_LABELS[placement]}</strong>
                </p>
                <p className={styles.hint}>
                  Assets et émotions viennent du tableau Personnages (metadata.characters).
                </p>
              </div>

              <div className={styles.field}>
                <label>Nom affiché (speaker)</label>
                <input
                  className={styles.input}
                  value={item.speaker ?? selectedChar.name}
                  onChange={(e) => onChange({ ...item, speaker: e.target.value })}
                />
              </div>

              <div className={styles.field}>
                <label>Émotion</label>
                {moodOptions.length > 0 ? (
                  <select
                    className={styles.select}
                    value={currentEmotion}
                    onChange={(e) => updateEmotion(e.target.value)}
                  >
                    {moodOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                ) : (
                  <p className={styles.hint}>
                    Définissez des émotions dans l&apos;onglet Personnages (+ Émotion → neutre).
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {item.isInternal && (
        <div className={styles.field}>
          <label>Speaker (optionnel)</label>
          <input className={styles.input} value={item.speaker ?? ''} onChange={(e) => onChange({ ...item, speaker: e.target.value })} />
        </div>
      )}

      <div className={styles.field}>
        <label>Texte (supporte **terme**(définition))</label>
        <textarea className={styles.textarea} rows={4} value={item.text} onChange={(e) => onChange({ ...item, text: e.target.value })} />
      </div>
      <SceneSelect value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
      <div className={styles.field}>
        <label>condition</label>
        <input className={styles.input} value={item.condition ?? ''} onChange={(e) => onChange({ ...item, condition: e.target.value || undefined })} />
      </div>

      <DialoguePreview
        scenarioId={scenarioId}
        background={sceneBackground}
        item={item}
        characters={characters}
        placement={placement}
        currentEmotion={currentEmotion}
      />
    </>
  );
}

function ChoiceForm({ item, sceneIds, characters, onChange }: ContentItemFormProps & { item: ChoiceItem }) {
  const updateOption = (idx: number, patch: Partial<ChoiceItem['options'][0]>) => {
    const options = item.options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    onChange({ ...item, options });
  };

  const setStopButton = (idx: number, checked: boolean) => {
    const options = item.options.map((o, i) => {
      if (i === idx) {
        return { ...o, stopButton: checked || undefined };
      }
      if (checked) {
        return { ...o, stopButton: undefined };
      }
      return o;
    });
    onChange({ ...item, options });
  };

  return (
    <>
      <div className={styles.field}>
        <label>Question / prompt</label>
        <input className={styles.input} value={item.prompt ?? ''} onChange={(e) => onChange({ ...item, prompt: e.target.value })} />
      </div>
      <h5>Options</h5>
      {item.options.map((opt, idx) => (
        <div key={idx} className={styles.card}>
          <div className={styles.field}>
            <label>Texte</label>
            <input className={styles.input} value={opt.text} onChange={(e) => updateOption(idx, { text: e.target.value })} />
          </div>
          <SceneSelect value={opt.goto} sceneIds={sceneIds} onChange={(goto) => updateOption(idx, { goto: goto || undefined })} />
          <div className={styles.field}>
            <label>pedaShields (bonne réponse)</label>
            <input type="number" className={styles.input} value={opt.pedaShields ?? 0} onChange={(e) => updateOption(idx, { pedaShields: parseInt(e.target.value, 10) || undefined })} />
          </div>
          {(opt.pedaShields ?? 0) > 0 && (
            <PedaCategorySelect
              value={opt.pedaCategory ?? ''}
              onChange={(pedaCategory) => updateOption(idx, { pedaCategory })}
              required
            />
          )}
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={Boolean(opt.stopButton)}
                onChange={(e) => setStopButton(idx, e.target.checked)}
              />
              {' '}Bouton STOP
            </label>
          </div>
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={(opt.confidenceGains?.length ?? 0) > 0}
                disabled={characters.length === 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    const firstChar = characters[0];
                    updateOption(idx, {
                      confidenceGains: opt.confidenceGains?.length
                        ? opt.confidenceGains
                        : firstChar
                          ? [{ characterId: firstChar.id, amount: 10 }]
                          : [],
                    });
                  } else {
                    updateOption(idx, { confidenceGains: undefined });
                  }
                }}
              />
              {' '}Confiance
            </label>
          </div>
          {(opt.confidenceGains?.length ?? 0) > 0 && (
            <>
              {opt.confidenceGains!.map((gain, gainIdx) => (
                <div key={gainIdx} className={styles.card}>
                  <div className={styles.field}>
                    <label>Personnage</label>
                    <select
                      className={styles.select}
                      value={gain.characterId}
                      onChange={(e) => {
                        const confidenceGains = [...(opt.confidenceGains ?? [])];
                        confidenceGains[gainIdx] = { ...gain, characterId: e.target.value };
                        updateOption(idx, { confidenceGains });
                      }}
                    >
                      {characters.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Points à ajouter (%)</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className={styles.input}
                      value={gain.amount}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        const confidenceGains = [...(opt.confidenceGains ?? [])];
                        confidenceGains[gainIdx] = { ...gain, amount: n > 0 ? Math.min(100, n) : 1 };
                        updateOption(idx, { confidenceGains });
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    onClick={() => {
                      const confidenceGains = (opt.confidenceGains ?? []).filter((_, i) => i !== gainIdx);
                      updateOption(idx, {
                        confidenceGains: confidenceGains.length > 0 ? confidenceGains : undefined,
                      });
                    }}
                  >
                    Retirer personnage
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => {
                  const usedIds = new Set((opt.confidenceGains ?? []).map((g) => g.characterId));
                  const nextChar = characters.find((c) => !usedIds.has(c.id)) ?? characters[0];
                  if (!nextChar) return;
                  updateOption(idx, {
                    confidenceGains: [...(opt.confidenceGains ?? []), { characterId: nextChar.id, amount: 10 }],
                  });
                }}
              >
                + Personnage
              </button>
            </>
          )}
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={Boolean(opt.trust)}
                disabled={characters.length === 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    const firstChar = characters[0];
                    updateOption(idx, {
                      trust: firstChar
                        ? { characterId: firstChar.id, points: 1 }
                        : undefined,
                    });
                  } else {
                    updateOption(idx, { trust: undefined });
                  }
                }}
              />
              {' '}Alliance (trust)
            </label>
          </div>
          {opt.trust && (
            <div className={styles.card}>
              <div className={styles.field}>
                <label>Allié</label>
                <select
                  className={styles.select}
                  value={opt.trust.characterId}
                  onChange={(e) => updateOption(idx, {
                    trust: { ...opt.trust!, characterId: e.target.value },
                  })}
                >
                  {characters.map((character) => (
                    <option key={character.id} value={character.id}>{character.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>Points d&apos;alliance</label>
                <input
                  type="number"
                  min={1}
                  className={styles.input}
                  value={opt.trust.points}
                  onChange={(e) => {
                    const points = parseInt(e.target.value, 10);
                    updateOption(idx, {
                      trust: {
                        ...opt.trust!,
                        points: points > 0 ? points : 1,
                      },
                    });
                  }}
                />
              </div>
            </div>
          )}
          <button type="button" className={styles.btnDanger} onClick={() => onChange({ ...item, options: item.options.filter((_, i) => i !== idx) })}>Supprimer option</button>
        </div>
      ))}
      <button type="button" className={styles.btnSecondary} onClick={() => onChange({ ...item, options: [...item.options, { text: 'Nouvelle option' }] })}>+ Option</button>
    </>
  );
}

function CommandForm({ item, onChange }: ContentItemFormProps & { item: CommandItem }) {
  return (
    <>
      <div className={styles.field}>
        <label>command</label>
        <select className={styles.select} value={item.command} onChange={(e) => onChange({ ...item, command: e.target.value as CommandItem['command'] })}>
          {['setFlag', 'setVar', 'collectItem', 'showElement', 'hideElement', 'animateElement', 'unlockSkill', 'unlockAchievement'].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label>target</label>
        <input className={styles.input} value={item.target} onChange={(e) => onChange({ ...item, target: e.target.value })} />
      </div>
      <div className={styles.field}>
        <label>value</label>
        <input className={styles.input} value={String(item.value ?? '')} onChange={(e) => onChange({ ...item, value: e.target.value })} />
      </div>
    </>
  );
}

function TextOverlayOptionsEditor({
  options,
  sceneIds,
  onChange,
}: {
  options: TextOverlayOption[];
  sceneIds: string[];
  onChange: (options: TextOverlayOption[]) => void;
}) {
  const updateOption = (idx: number, patch: Partial<TextOverlayOption>) => {
    onChange(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };

  return (
    <>
      <h5>Options</h5>
      {options.map((opt, idx) => (
        <div key={idx} className={styles.card}>
          <div className={styles.field}>
            <label>Texte</label>
            <input className={styles.input} value={opt.text} onChange={(e) => updateOption(idx, { text: e.target.value })} />
          </div>
          <SceneSelect value={opt.goto} sceneIds={sceneIds} onChange={(goto) => updateOption(idx, { goto: goto || undefined })} />
          <div className={styles.field}>
            <label>condition</label>
            <input className={styles.input} value={opt.condition ?? ''} onChange={(e) => updateOption(idx, { condition: e.target.value || undefined })} />
          </div>
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={opt.closeOverlay !== false}
                onChange={(e) => updateOption(idx, { closeOverlay: e.target.checked ? undefined : false })}
              />
              {' '}Fermer l&apos;overlay après clic
            </label>
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => onChange(options.filter((_, i) => i !== idx))}>
            Supprimer option
          </button>
        </div>
      ))}
      <button type="button" className={styles.btnSecondary} onClick={() => onChange([...options, { text: 'Continuer' }])}>
        + Option
      </button>
    </>
  );
}

function ChapterEndForm({ item, onChange }: ContentItemFormProps & { item: ChapterEndItem }) {
  const updatePerfRow = (idx: number, patch: Partial<ChapterEndPerfRow>) => {
    const perfRows = item.perfRows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange({ ...item, perfRows });
  };

  return (
    <>
      <div className={styles.field}>
        <label>Surtitre (eyebrow)</label>
        <input className={styles.input} value={item.eyebrow} onChange={(e) => onChange({ ...item, eyebrow: e.target.value })} />
      </div>
      <div className={styles.field}>
        <label>Titre (heading)</label>
        <input className={styles.input} value={item.heading} onChange={(e) => onChange({ ...item, heading: e.target.value })} />
      </div>
      <div className={styles.field}>
        <label>Libellé mission</label>
        <input className={styles.input} value={item.missionLabel ?? ''} onChange={(e) => onChange({ ...item, missionLabel: e.target.value || undefined })} />
      </div>
      <div className={styles.field}>
        <label>Index mission</label>
        <input type="number" min={1} className={styles.input} value={item.missionIndex} onChange={(e) => onChange({ ...item, missionIndex: parseInt(e.target.value, 10) || 1 })} />
      </div>
      <div className={styles.field}>
        <label>Total missions</label>
        <input type="number" min={1} className={styles.input} value={item.missionTotal} onChange={(e) => onChange({ ...item, missionTotal: parseInt(e.target.value, 10) || 1 })} />
      </div>
      <div className={styles.field}>
        <label>Boucliers max chapitre</label>
        <input type="number" min={0} className={styles.input} value={item.chapterShieldsMax ?? 0} onChange={(e) => onChange({ ...item, chapterShieldsMax: parseInt(e.target.value, 10) || undefined })} />
      </div>
      <div className={styles.field}>
        <label>Légende boucliers chapitre</label>
        <input className={styles.input} value={item.chapterShieldsCaption ?? ''} onChange={(e) => onChange({ ...item, chapterShieldsCaption: e.target.value || undefined })} />
      </div>
      <div className={styles.field}>
        <label>Idée clé (leçon)</label>
        <textarea className={styles.textarea} rows={3} value={item.keyIdea} onChange={(e) => onChange({ ...item, keyIdea: e.target.value })} />
      </div>
      <h5>Réflexes</h5>
      {item.reflexes.map((reflex, idx) => (
        <div key={idx} className={styles.card}>
          <div className={styles.field}>
            <label>Réflexe {idx + 1}</label>
            <textarea className={styles.textarea} rows={2} value={reflex} onChange={(e) => {
              const reflexes = [...item.reflexes];
              reflexes[idx] = e.target.value;
              onChange({ ...item, reflexes });
            }} />
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => onChange({ ...item, reflexes: item.reflexes.filter((_, i) => i !== idx) })}>
            Supprimer
          </button>
        </div>
      ))}
      <button type="button" className={styles.btnSecondary} onClick={() => onChange({ ...item, reflexes: [...item.reflexes, ''] })}>
        + Réflexe
      </button>
      <div className={styles.field}>
        <label>Note performance (perfNote)</label>
        <input className={styles.input} value={item.perfNote ?? ''} onChange={(e) => onChange({ ...item, perfNote: e.target.value || undefined })} />
      </div>
      <h5>Lignes de performance</h5>
      {item.perfRows.map((row, idx) => (
        <div key={idx} className={styles.card}>
          <div className={styles.field}>
            <label>Label</label>
            <input className={styles.input} value={row.label} onChange={(e) => updatePerfRow(idx, { label: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Texte</label>
            <textarea className={styles.textarea} rows={2} value={row.text} onChange={(e) => updatePerfRow(idx, { text: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Variante</label>
            <select className={styles.select} value={row.variant ?? 'default'} onChange={(e) => updatePerfRow(idx, { variant: e.target.value as ChapterEndPerfRow['variant'] })}>
              <option value="default">default</option>
              <option value="trust">trust</option>
            </select>
          </div>
          <div className={styles.field}>
            <label>condition</label>
            <input className={styles.input} value={row.condition ?? ''} onChange={(e) => updatePerfRow(idx, { condition: e.target.value || undefined })} />
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => onChange({ ...item, perfRows: item.perfRows.filter((_, i) => i !== idx) })}>
            Supprimer ligne
          </button>
        </div>
      ))}
      <button type="button" className={styles.btnSecondary} onClick={() => onChange({ ...item, perfRows: [...item.perfRows, { label: '', text: '' }] })}>
        + Ligne performance
      </button>
      <div className={styles.field}>
        <label>Libellé du bouton continuer</label>
        <input
          className={styles.input}
          value={item.continueText ?? ''}
          onChange={(e) => onChange({ ...item, continueText: e.target.value || undefined })}
          placeholder="Continuer"
        />
      </div>
      <p className={styles.hint}>
        Le bouton ≡ Menu est toujours affiché en jeu. La suite passe automatiquement à la scène suivante dans le flux.
      </p>
    </>
  );
}

function LegacyChapterEndOverlayForm({
  scenarioId,
  item,
  sceneIds,
  onChange,
}: ContentItemFormProps & { item: TextOverlayItem }) {
  const data = item.chapterEnd ?? defaultChapterEndData();
  const chapterItem: ChapterEndItem = {
    id: item.id,
    type: 'chapterEnd',
    ...data,
    continueText: item.continueText ?? data.continueText,
  };

  return (
    <>
      <p className={styles.hint}>
        Format legacy (overlay texte / chapterEnd). Préférez le type de contenu « Fin de chapitre » pour les nouvelles scènes.
      </p>
      <ChapterEndForm
        scenarioId={scenarioId}
        item={chapterItem}
        sceneIds={sceneIds}
        characters={[]}
        onChange={(updated) => {
          if (updated.type !== 'chapterEnd') return;
          const {
            id: _id,
            type: _type,
            continueText,
            background: _bg,
            elements: _el,
            condition: _cond,
            goto: _goto,
            ...chapterEnd
          } = updated;
          onChange({ ...item, chapterEnd, continueText });
        }}
      />
    </>
  );
}

function TextOverlayForm({ scenarioId, item, sceneIds, characters, onChange }: ContentItemFormProps & { item: TextOverlayItem }) {
  if (item.overlayType === 'chapterEnd') {
    return (
      <LegacyChapterEndOverlayForm
        scenarioId={scenarioId}
        item={item}
        sceneIds={sceneIds}
        characters={[]}
        onChange={onChange}
      />
    );
  }

  return (
    <>
      <div className={styles.field}>
        <label>overlayType</label>
        <select className={styles.select} value={item.overlayType ?? 'letter'} onChange={(e) => onChange({ ...item, overlayType: e.target.value as TextOverlayItem['overlayType'] })}>
          {['letter', 'cyber', 'email', 'console', 'terminal', 'poster', 'note', 'document', 'summary'].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label>Titre</label>
        <input className={styles.input} value={item.title ?? ''} onChange={(e) => onChange({ ...item, title: e.target.value })} />
      </div>
      {((item.overlayType ?? 'letter') === 'letter' || item.overlayType === 'cyber') && (
        <>
          <div className={styles.field}>
            <label>Libellé canal</label>
            <input
              className={styles.input}
              value={item.channelLabel ?? ''}
              onChange={(e) => onChange({ ...item, channelLabel: e.target.value.trim() || undefined })}
              placeholder={(item.overlayType ?? 'letter') === 'cyber' ? 'Canal sécurisé' : 'Mission 1'}
            />
          </div>
          <div className={styles.field}>
            <label>Nom du personnage</label>
            <input
              className={styles.input}
              value={item.characterName ?? ''}
              onChange={(e) => onChange({ ...item, characterName: e.target.value || undefined })}
              placeholder="Natalie — Direction"
            />
          </div>
          <div className={styles.field}>
            <label>Icône du personnage</label>
            <input
              className={styles.input}
              value={item.characterIcon ?? ''}
              onChange={(e) => onChange({ ...item, characterIcon: e.target.value || undefined })}
              placeholder="assets/characters/…"
            />
          </div>
          {characters.length > 0 && (
            <div className={styles.field}>
              <label>Remplir depuis personnage</label>
              <select
                className={styles.select}
                value=""
                onChange={(e) => {
                  const char = characters.find((c) => c.id === e.target.value);
                  if (!char) return;
                  onChange({
                    ...item,
                    characterName: char.name,
                    characterIcon: char.icon || char.defaultImage,
                  });
                }}
              >
                <option value="">—</option>
                {characters.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.id}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      <div className={styles.field}>
        <label>Contenu</label>
        <textarea className={styles.textarea} rows={4} value={item.content} onChange={(e) => onChange({ ...item, content: e.target.value })} />
      </div>
      <div className={styles.field}>
        <label>Libellé du bouton continuer</label>
        <input
          className={styles.input}
          value={item.continueText ?? ''}
          onChange={(e) => onChange({ ...item, continueText: e.target.value || undefined })}
          placeholder="Utilisé si aucune option — ex. Continuer"
        />
      </div>
      <TextOverlayOptionsEditor
        options={item.options ?? []}
        sceneIds={sceneIds}
        onChange={(options) => onChange({ ...item, options })}
      />
    </>
  );
}

function PhoneMailForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: PhoneMailItem }) {
  const isMulti = (item.mails?.length ?? 0) > 0;

  return (
    <>
      <div className={styles.phoneSection}>
        <h5>Paramètres généraux</h5>
        <div className={styles.field}>
          <label>Mails non lus (unreadCount)</label>
          <input
            type="number"
            className={styles.input}
            value={item.unreadCount ?? ''}
            onChange={(e) =>
              onChange({ ...item, unreadCount: parseInt(e.target.value, 10) || undefined })
            }
          />
        </div>
        <div className={styles.field}>
          <label>Libellé du bouton continuer</label>
          <input
            className={styles.input}
            value={item.continueLabel ?? ''}
            onChange={(e) => onChange({ ...item, continueLabel: e.target.value || undefined })}
            placeholder="Fermer"
          />
        </div>
        <div className={styles.field}>
          <label>Progression (ex. Mail 1/3)</label>
          <input
            className={styles.input}
            value={item.mailProgress ?? ''}
            onChange={(e) => onChange({ ...item, mailProgress: e.target.value || undefined })}
          />
        </div>
        <div className={styles.field}>
          <label>Boucliers pédagogiques max (session)</label>
          <input
            type="number"
            className={styles.input}
            value={item.pedaShieldsMax ?? ''}
            onChange={(e) =>
              onChange({ ...item, pedaShieldsMax: parseInt(e.target.value, 10) || undefined })
            }
          />
        </div>
        <div className={styles.field}>
          <label>
            <input
              type="checkbox"
              checked={item.startInReading === true}
              onChange={(e) => onChange({ ...item, startInReading: e.target.checked || undefined })}
            />
            {' '}Ouvrir directement sur le corps du mail
          </label>
        </div>
        <SceneSelect
          value={item.goto}
          sceneIds={sceneIds}
          onChange={(goto) => onChange({ ...item, goto: goto || undefined })}
        />
      </div>

      <PhoneMailTriageMailsEditor
        mails={item.mails ?? []}
        onChange={(mails) =>
          onChange({ ...item, mails: mails.length > 0 ? mails : undefined })
        }
      />

      {!isMulti && (
        <div className={styles.phoneSection}>
          <h5>Mail affiché</h5>
          <div className={styles.field}>
            <label>De</label>
            <input className={styles.input} value={item.from ?? ''} onChange={(e) => onChange({ ...item, from: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Objet</label>
            <input className={styles.input} value={item.subject ?? ''} onChange={(e) => onChange({ ...item, subject: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Aperçu (liste)</label>
            <input className={styles.input} value={item.preview ?? ''} onChange={(e) => onChange({ ...item, preview: e.target.value || undefined })} />
          </div>
          <div className={styles.field}>
            <label>Heure</label>
            <input className={styles.input} value={item.receivedAt ?? ''} onChange={(e) => onChange({ ...item, receivedAt: e.target.value || undefined })} placeholder="06:47" />
          </div>
          <div className={styles.field}>
            <label>Contenu</label>
            <textarea className={styles.textarea} rows={6} value={item.content ?? ''} onChange={(e) => onChange({ ...item, content: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Note pièces jointes</label>
            <input
              className={styles.input}
              value={item.attachmentsNote ?? ''}
              onChange={(e) => onChange({ ...item, attachmentsNote: e.target.value || undefined })}
              placeholder="11 fichiers — le plus petit : 4,2 Mo"
            />
          </div>

          <PhoneMailAttachmentsEditor
            attachments={item.attachments ?? []}
            onChange={(attachments) =>
              onChange({ ...item, attachments: attachments.length > 0 ? attachments : undefined })
            }
          />

          <PhoneMailInboxItemsEditor
            items={item.inboxItems ?? []}
            onChange={(inboxItems) =>
              onChange({ ...item, inboxItems: inboxItems.length > 0 ? inboxItems : undefined })
            }
          />

          <PhoneMailTriageActionsEditor
            actions={item.triageActions ?? []}
            onChange={(triageActions) =>
              onChange({
                ...item,
                triageActions: triageActions.length > 0 ? triageActions : undefined,
              })
            }
          />
        </div>
      )}
    </>
  );
}

function PhoneCallForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: PhoneCallItem }) {
  const callStyle = resolvePhoneCallStyle(item);
  const isPhoneStyle = callStyle === 'phone';

  return (
    <>
      <div className={styles.field}>
        <label>Style</label>
        <select
          className={styles.select}
          value={callStyle}
          onChange={(e) =>
            onChange({ ...item, style: e.target.value as PhoneCallItem['style'] })
          }
        >
          <option value="inline_call">Appel overlay</option>
          <option value="phone">Téléphone</option>
        </select>
      </div>
      <div className={styles.field}>
        <label>Nom du contact</label>
        <input
          className={styles.input}
          value={item.contactName}
          onChange={(e) => onChange({ ...item, contactName: e.target.value })}
        />
      </div>
      {!isPhoneStyle && (
        <div className={styles.field}>
          <label>Rôle / fonction (overlay)</label>
          <input
            className={styles.input}
            value={item.contactRole ?? ''}
            onChange={(e) => onChange({ ...item, contactRole: e.target.value || undefined })}
            placeholder="La Directrice"
          />
        </div>
      )}
      <div className={styles.field}>
        <label>Avatar (chemin asset, optionnel)</label>
        <input
          className={styles.input}
          value={item.contactAvatar ?? ''}
          onChange={(e) => onChange({ ...item, contactAvatar: e.target.value || undefined })}
          placeholder="assets/characters/…"
        />
      </div>
      <div className={styles.field}>
        <label>Délai entre messages (ms)</label>
        <input
          type="number"
          className={styles.input}
          value={item.messageDelay ?? 3000}
          onChange={(e) =>
            onChange({ ...item, messageDelay: parseInt(e.target.value, 10) || undefined })
          }
        />
      </div>
      <div className={styles.field}>
        <label>
          <input
            type="checkbox"
            checked={item.incomingCall === true}
            onChange={(e) => onChange({ ...item, incomingCall: e.target.checked || undefined })}
          />
          {' '}
          {isPhoneStyle ? 'Appel entrant (écran sonnerie)' : 'Appel entrant (libellé carte)'}
        </label>
      </div>
      {isPhoneStyle && item.incomingCall && (
        <>
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={item.autoAnswer === true}
                onChange={(e) => onChange({ ...item, autoAnswer: e.target.checked || undefined })}
              />
              {' '}Décrocher automatiquement
            </label>
          </div>
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={item.allowDecline === true}
                onChange={(e) => onChange({ ...item, allowDecline: e.target.checked || undefined })}
              />
              {' '}Autoriser refuser une fois
            </label>
          </div>
          <div className={styles.field}>
            <label>Durée sonnerie (ms)</label>
            <input
              type="number"
              className={styles.input}
              value={item.ringDurationMs ?? ''}
              onChange={(e) =>
                onChange({
                  ...item,
                  ringDurationMs: parseInt(e.target.value, 10) || undefined,
                })
              }
              placeholder="2400"
            />
          </div>
        </>
      )}

      <PhoneCallMessagesEditor
        messages={item.messages}
        onChange={(messages) => onChange({ ...item, messages })}
      />

      <PhoneReplyOptionsEditor
        options={item.replyOptions ?? []}
        sceneIds={sceneIds}
        onChange={(replyOptions) =>
          onChange({
            ...item,
            replyOptions: replyOptions.length > 0 ? replyOptions : undefined,
          })
        }
      />

      {!item.replyOptions?.length && (
        <SceneSelect
          label="Scène suivante (si pas de réponses joueur)"
          value={item.goto}
          sceneIds={sceneIds}
          onChange={(goto) => onChange({ ...item, goto: goto || undefined })}
        />
      )}
    </>
  );
}

function PhoneChatForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: PhoneChatItem }) {
  return (
    <>
      <div className={styles.field}><label>contactName</label><input className={styles.input} value={item.contactName} onChange={(e) => onChange({ ...item, contactName: e.target.value })} /></div>
      <JsonEditor label="messages" value={item.messages} onChange={(messages) => onChange({ ...item, messages })} />
      <JsonEditor label="replyOptions" value={item.replyOptions ?? []} onChange={(replyOptions) => onChange({ ...item, replyOptions })} />
      <SceneSelect value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function PhoneMailInteractForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: PhoneMailInteractItem }) {
  return (
    <>
      <div className={styles.field}><label>from</label><input className={styles.input} value={item.from ?? ''} onChange={(e) => onChange({ ...item, from: e.target.value })} /></div>
      <div className={styles.field}><label>subject</label><input className={styles.input} value={item.subject ?? ''} onChange={(e) => onChange({ ...item, subject: e.target.value })} /></div>
      <SceneSelect label="goto" value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function SituationSortForm({ item, characters, onChange }: ContentItemFormProps & { item: SituationSortGameItem }) {
  return (
    <SituationSortGameEditor
      item={item}
      characters={characters}
      onChange={(patch) => onChange({ ...item, ...patch })}
    />
  );
}

function PasswordGameForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: PasswordGameItem }) {
  return (
    <>
      <JsonEditor label="rounds" value={item.rounds} onChange={(rounds) => onChange({ ...item, rounds })} />
      <div className={styles.field}><label>victoryReward</label><input className={styles.input} value={item.victoryReward} onChange={(e) => onChange({ ...item, victoryReward: e.target.value })} /></div>
      <JsonEditor label="screenPosition" value={item.screenPosition ?? { top: 5, left: 17, width: 66, height: 58 }} onChange={(screenPosition) => onChange({ ...item, screenPosition })} />
      <SceneSelect label="goto" value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function DragDropGameForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: DragDropGameItem }) {
  return (
    <>
      <JsonEditor label="phrases" value={item.phrases} onChange={(phrases) => onChange({ ...item, phrases })} />
      <div className={styles.field}><label>requiredSelections</label><input type="number" className={styles.input} value={item.requiredSelections} onChange={(e) => onChange({ ...item, requiredSelections: parseInt(e.target.value, 10) })} /></div>
      <JsonEditor label="resultRules" value={item.resultRules} onChange={(resultRules) => onChange({ ...item, resultRules })} />
      <SceneSelect label="goto" value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function TimedOverlayForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: TimedOverlayItem }) {
  return (
    <>
      <div className={styles.field}><label>message</label><input className={styles.input} value={item.message} onChange={(e) => onChange({ ...item, message: e.target.value })} /></div>
      <div className={styles.field}><label>durationMs</label><input type="number" className={styles.input} value={item.durationMs} onChange={(e) => onChange({ ...item, durationMs: parseInt(e.target.value, 10) })} /></div>
      <SceneSelect value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function ConfidenceGainForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: ConfidenceGainItem }) {
  return (
    <>
      <JsonEditor label="characters" value={item.characters} onChange={(characters) => onChange({ ...item, characters })} />
      <SceneSelect value={item.goto} sceneIds={sceneIds} onChange={(goto) => onChange({ ...item, goto: goto || undefined })} />
    </>
  );
}

function RecordCardForm({ item, onChange }: ContentItemFormProps & { item: RecordCardItem }) {
  const takenText = (item.taken ?? []).join('\n');
  const savedText = (item.saved ?? []).join('\n');

  return (
    <>
      <div className={styles.field}>
        <label>Titre (nom)</label>
        <input
          className={styles.input}
          value={item.title}
          onChange={(e) => onChange({ ...item, title: e.target.value })}
        />
      </div>
      <div className={styles.field}>
        <label>Sous-titre</label>
        <input
          className={styles.input}
          value={item.subtitle ?? ''}
          placeholder="dite prestataire de convoyage"
          onChange={(e) => onChange({ ...item, subtitle: e.target.value || undefined })}
        />
      </div>
      <div className={styles.field}>
        <label>Résumé</label>
        <textarea
          className={styles.textarea}
          value={item.summary ?? ''}
          onChange={(e) => onChange({ ...item, summary: e.target.value || undefined })}
          rows={3}
        />
      </div>
      <div className={styles.field}>
        <label>Portrait (chemin asset)</label>
        <input
          className={styles.input}
          value={item.image ?? ''}
          onChange={(e) => onChange({ ...item, image: e.target.value || undefined })}
        />
      </div>
      <div className={styles.field}>
        <label>Matricule</label>
        <input
          className={styles.input}
          value={item.matricule ?? ''}
          placeholder="QUAI · 10H52"
          onChange={(e) => onChange({ ...item, matricule: e.target.value || undefined })}
        />
      </div>
      <JsonEditor
        label="serial (optionnel)"
        value={item.serial ?? { serie: 'LES TOMBÉS', n: 1, total: 12 }}
        onChange={(serial) => onChange({ ...item, serial })}
      />
      <JsonEditor
        label="danger (optionnel)"
        value={item.danger ?? { score: 5, max: 10, word: '' }}
        onChange={(danger) => onChange({ ...item, danger })}
      />
      <div className={styles.field}>
        <label>Parti avec (une ligne par item)</label>
        <textarea
          className={styles.textarea}
          value={takenText}
          onChange={(e) =>
            onChange({
              ...item,
              taken: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean),
            })
          }
          rows={4}
        />
      </div>
      <div className={styles.field}>
        <label>Sauvé (une ligne par item)</label>
        <textarea
          className={styles.textarea}
          value={savedText}
          onChange={(e) =>
            onChange({
              ...item,
              saved: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean),
            })
          }
          rows={4}
        />
      </div>
      <JsonEditor
        label="stamp"
        value={item.stamp ?? { text: 'COFFRÉE', sub: 'MISSION 1' }}
        onChange={(stamp) => onChange({ ...item, stamp })}
      />
      <div className={styles.field}>
        <label>continueText</label>
        <input
          className={styles.input}
          value={item.continueText ?? ''}
          placeholder="Continuer →"
          onChange={(e) => onChange({ ...item, continueText: e.target.value || undefined })}
        />
      </div>
      <div className={styles.field}>
        <label>nextHint (optionnel)</label>
        <input
          className={styles.input}
          value={item.nextHint ?? ''}
          placeholder="MISSION 5 DANS UN INSTANT"
          onChange={(e) => onChange({ ...item, nextHint: e.target.value || undefined })}
        />
      </div>
    </>
  );
}

function GameOverForm({ item, sceneIds, onChange }: ContentItemFormProps & { item: GameOverItem }) {
  return (
    <>
      <div className={styles.field}>
        <label>Titre</label>
        <input
          className={styles.input}
          value={item.heading ?? ''}
          placeholder="GAME OVER"
          onChange={(e) => onChange({ ...item, heading: e.target.value || undefined })}
        />
      </div>
      <div className={styles.field}>
        <label>Explication</label>
        <textarea
          className={styles.textarea}
          value={item.text}
          onChange={(e) => onChange({ ...item, text: formatGameOverText(e.target.value) })}
          rows={5}
        />
      </div>
      <div className={styles.field}>
        <label>Libellé bouton</label>
        <input
          className={styles.input}
          value={item.restartLabel ?? ''}
          placeholder="Recommencer"
          onChange={(e) => onChange({ ...item, restartLabel: e.target.value || undefined })}
        />
      </div>
      <SceneSelect
        label="goto (recommencer à)"
        value={item.goto}
        sceneIds={sceneIds}
        onChange={(goto) => onChange({ ...item, goto: goto || undefined })}
      />
      <JsonEditor
        label="actions (optionnel)"
        value={item.actions ?? []}
        onChange={(actions) => onChange({ ...item, actions: actions.length ? actions : undefined })}
      />
    </>
  );
}

export const ContentItemForm: React.FC<ContentItemFormProps> = (props) => {
  const { item } = props;
  switch (item.type) {
    case 'dialogue':
      return <DialogueForm {...props} item={item} />;
    case 'choice':
      return <ChoiceForm {...props} item={item} />;
    case 'command':
      return <CommandForm {...props} item={item} />;
    case 'textOverlay':
      return <TextOverlayForm {...props} item={item} />;
    case 'chapterEnd':
      return <ChapterEndForm {...props} item={item} />;
    case 'phoneMail':
      return <PhoneMailForm {...props} item={item} />;
    case 'phoneCall':
      return <PhoneCallForm {...props} item={item} />;
    case 'phoneChat':
      return <PhoneChatForm {...props} item={item} />;
    case 'phoneMailInteract':
      return <PhoneMailInteractForm {...props} item={item} />;
    case 'situationSortGame':
      return <SituationSortForm {...props} item={item} />;
    case 'passwordGame':
      return <PasswordGameForm {...props} item={item} />;
    case 'dragDropGame':
      return <DragDropGameForm {...props} item={item} />;
    case 'timedOverlay':
      return <TimedOverlayForm {...props} item={item} />;
    case 'confidenceGain':
      return <ConfidenceGainForm {...props} item={item} />;
    case 'gameOver':
      return <GameOverForm {...props} item={item} />;
    case 'recordCard':
      return <RecordCardForm {...props} item={item} />;
    default:
      return <JsonEditor label="content item" value={item} onChange={props.onChange} />;
  }
};
